import { useAuth } from "@/auth/contexts/AuthProvider";
import CopyToClipboardButton from "@/core/components/v2/CopyToClipboardButton";
import { useSnackbar } from "@/core/contexts/SnackbarProvider";
import { VideoAsset } from "@/shared-types/v2";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import MuxPlayer from "@mux/mux-player-react";
import React, { useEffect, useState } from "react";
const VideoPreview = ({
  videoAsset,
  onClose,
}: {
  videoAsset?: VideoAsset | null;
  videoAssets?: VideoAsset[] | null;
  onClose: () => void;
}) => {
  const [fullScreen, setFullScreen] = React.useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const { equestrain } = useAuth();
  // lets not use generic data loading state in this component but instead declare our own
  const [dataLoading, setDataLoading] = useState(true);
  const snackbar = useSnackbar();

  const previewVideo = async (video: VideoAsset) => {
    // skip updating generic data loading state
    equestrain.Client.setSkipOnDataLoadingFunctions(true);
    setDataLoading(true);
    const { data, error } = await equestrain.Client.watchVideo(video);
    setDataLoading(false);
    // reset the generic data loading state
    equestrain.Client.setSkipOnDataLoadingFunctions(false);
    if (error) {
      snackbar.error("Error loading video: " + error.message);
      return;
    }
    setPreviewVideoUrl(data);
  };

  useEffect(() => {
    if (videoAsset) {
      previewVideo(videoAsset);
    }
    return () => {
      setPreviewVideoUrl(null);
    };
  // Deps intentionally omitted: previewVideo is redefined on every render, so listing it would re-run this effect continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoAsset]);

  return (
    <Dialog
      open={videoAsset !== null && videoAsset !== undefined}
      onClose={onClose}
      aria-labelledby="videopreview-title"
      fullScreen={fullScreen}
    >
      <DialogTitle id="videopreview-title">
        Video preview {videoAsset?.title && `(${videoAsset.title})`}
      </DialogTitle>
      <DialogContent>
        {dataLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {previewVideoUrl && (
          <MuxPlayer
            src={previewVideoUrl}
            autoPlay
            metadata={{
              video_id: videoAsset?.id,
              video_title: videoAsset?.title,
              // viewer_user_id: "",
            }}
            streamType="on-demand"
          />
        )}
      </DialogContent>
      <DialogActions>
        <CopyToClipboardButton
          value={videoAsset?.id || ""}
          toolTip="Copy video ID"
          onValueCopied={() => snackbar.success("id copied to clipboard")}
        />
        <Button
          onClick={() => setFullScreen(!fullScreen)}
          variant={"contained"}
          color="info"
        >
          Fullscreen modal {fullScreen ? "off" : "on"}
        </Button>
        <Button onClick={onClose} variant={"contained"} color="primary">
          Close preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoPreview;
